import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { Globe, Menu, X, Heart } from 'lucide-react';

export const Navbar = () => {
  const { language, switchLanguage, t } = useLanguage();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const paypalDonateUrl = "https://www.paypal.com/donate?business=Hortensiangoundo86%40yahoo.com&currency_code=EUR";

  const navLinks = [
    { path: '/', label: t.nav.home },
    { path: '/services', label: t.nav.services },
    { path: '/listings', label: t.nav.listings },
    { path: '/news', label: t.nav.news },
    { path: '/about', label: t.nav.about },
    { path: '/contact', label: t.nav.contact },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav className="bg-white/80 backdrop-blur-md border-b border-stone-200 sticky top-0 z-50">
      <div className="container mx-auto px-4 md:px-8 lg:px-12">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
            <img src="/logo.jpg" alt="AILE" className="h-12 w-auto" />
            <span className="font-bold text-xl text-[#127abb] hidden sm:block">AILE</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-${link.path.slice(1) || 'home'}`}
                className={`font-medium transition-colors ${
                  isActive(link.path)
                    ? 'text-[#127abb] border-b-2 border-[#127abb]'
                    : 'text-slate-700 hover:text-[#127abb]'
                } pb-1`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Language Switcher & Donate */}
          <div className="flex items-center gap-3">
            <button
              data-testid="language-switcher"
              onClick={() => switchLanguage(language === 'fr' ? 'en' : 'fr')}
              className="flex items-center gap-2 px-3 py-2 rounded-full bg-primary-50 text-[#127abb] hover:bg-primary-100 transition-colors font-medium text-sm"
            >
              <Globe size={16} />
              <span>{language.toUpperCase()}</span>
            </button>

            {/* Donate Button */}
            <a
              href={paypalDonateUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="donate-btn-navbar"
              className="hidden md:flex items-center gap-2 px-4 py-2 bg-[#FFC439] hover:bg-[#F0B429] text-slate-900 font-semibold rounded-full transition-all hover:scale-105 shadow-md text-sm"
            >
              <Heart size={16} className="text-red-500" />
              {language === 'fr' ? 'Faire un don' : 'Donate'}
            </a>

            {/* Mobile menu button */}
            <button
              data-testid="mobile-menu-btn"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-3 text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
              aria-label="Menu"
            >
              {mobileMenuOpen ? <X size={28} strokeWidth={2} /> : <Menu size={28} strokeWidth={2} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden absolute top-full left-0 right-0 bg-white/95 backdrop-blur-lg border-t border-stone-200 shadow-2xl animate-in slide-in-from-top duration-300 z-50">
            <div className="container mx-auto px-4 py-6">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`block py-4 px-6 rounded-xl font-semibold text-lg transition-all duration-200 ${
                    isActive(link.path) 
                      ? 'text-white bg-[#127abb] shadow-lg' 
                      : 'text-slate-700 hover:bg-primary-50 hover:text-[#127abb] hover:translate-x-2'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              <div className="border-t border-stone-200 my-6"></div>
              
              {/* Donate Button Mobile */}
              <a
                href={paypalDonateUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center justify-center gap-2 py-4 px-6 rounded-xl font-semibold text-lg bg-[#FFC439] hover:bg-[#F0B429] text-slate-900 shadow-lg transition-all duration-200"
              >
                <Heart size={20} className="text-red-500" />
                {language === 'fr' ? 'Faire un don' : 'Donate'}
              </a>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};
